import { Box, Center, Flex, Skeleton } from '@chakra-ui/react';
import { AppLoadingSpinner } from '@/components/shared/AppLoadingSpinner';

interface AppRouteLoadingProps {
  minH?: string;
}

interface AppContentLoadingProps {
  minH?: string;
  dataSection?: string;
}

export function AppContentLoading({
  minH = '180px',
  dataSection = 'appContentLoadingSpinnerCenter',
}: AppContentLoadingProps) {
  return (
    <Center data-section={dataSection} w="full" minH={minH}>
      <AppLoadingSpinner minH={minH} />
    </Center>
  );
}

/**
 * Keep shell geometry while bootstrap is pending, without implying the user
 * has signed out or mounting account/project-dependent shell providers.
 */
export function AppRouteLoading({ minH = '100dvh' }: AppRouteLoadingProps) {
  return (
    <Box
      data-section="appRouteLoadingShell"
      h={minH}
      minH={minH}
      w="100vw"
      maxW="100vw"
      overflow="hidden"
      bg="app.bg"
      display="flex"
    >
      <Flex
        aria-hidden="true"
        display={{ base: 'none', lg: 'flex' }}
        direction="column"
        align="center"
        w="60px"
        flexShrink={0}
        py="3"
        gap="3"
      >
        {Array.from({ length: 7 }, (_, index) => (
          <Skeleton key={index} boxSize="10" borderRadius="14px" />
        ))}
      </Flex>
      <Flex
        flex="1"
        minW={0}
        minH={0}
        overflow="hidden"
        my={{ base: 0, lg: 1 }}
        mr={{ base: 0, lg: 3 }}
        borderRadius={{ base: 0, lg: '20px' }}
        borderWidth={{ base: 0, lg: '1px' }}
        borderColor="ui.border.subtle"
        bg="ui.surface.panel"
      >
        <Flex
          aria-hidden="true"
          display={{ base: 'none', lg: 'flex' }}
          direction="column"
          w="var(--attune-app-rail-width, 296px)"
          flexShrink={0}
          overflow="hidden"
          borderRightWidth="1px"
          borderColor="ui.border.divider"
          bg="ui.surface.rail"
        >
          <Box minW="296px" px="3" py="2.5">
            <Skeleton h="38px" borderRadius="12px" />
            <Skeleton h="6" w="24" mt="6" mb="5" />
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} h="8" mb="2" />
            ))}
          </Box>
        </Flex>
        <Flex flex="1" minW={0} direction="column">
          <Box
            aria-hidden="true"
            h="54px"
            flexShrink={0}
            borderBottomWidth="1px"
            borderColor="ui.border.subtle"
          />
          <Center flex="1" minH={0}>
            <AppContentLoading dataSection="appRouteLoadingSpinnerCenter" />
          </Center>
        </Flex>
      </Flex>
    </Box>
  );
}
